import React, { createContext, ReactNode, useContext } from 'react'

export enum BreakpointIdentifier {
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
}

export interface BreakpointsConfiguration {
  breakpoints: Record<BreakpointIdentifier, number>
  aliases: {
    mobile: BreakpointIdentifier[]
    tablet: BreakpointIdentifier[]
    desktop: BreakpointIdentifier[]
  }
}

const defaultBreakpointsConfiguration: BreakpointsConfiguration = {
  breakpoints: {
    // These points are based on Bootstrap 4 responsive breakpoints:
    // https://getbootstrap.com/docs/4.1/layout/overview/
    // The important consideration is that we want an accurate breakpoints to
    // detect mobile devices, which is now set to 0-576px wide. Tablet starts
    // at 576px and ends at 992px, beyond which desktop starts.
    // The sub thresholds may be used by exception for more fine-tuning, but
    // mobile, tablet and desktop thresholds should suffice in most instances.
    [BreakpointIdentifier.xs]: 0, // start mobile
    [BreakpointIdentifier.sm]: 576, // landscape phones
    [BreakpointIdentifier.md]: 768, // start tablet
    [BreakpointIdentifier.lg]: 992, // start "desktop" / tablet landscape
    [BreakpointIdentifier.xl]: 1310,
  },
  aliases: {
    mobile: [BreakpointIdentifier.xs, BreakpointIdentifier.sm],
    tablet: [BreakpointIdentifier.md],
    desktop: [BreakpointIdentifier.lg, BreakpointIdentifier.xl],
  },
}

export const BreakpointsContext = createContext<BreakpointsConfiguration>(
  defaultBreakpointsConfiguration,
)

interface BreakpointsProviderProps {
  children: ReactNode
  configuration?: BreakpointsConfiguration
}

export function BreakpointsProvider({
  children,
  configuration = defaultBreakpointsConfiguration,
}: BreakpointsProviderProps) {
  return (
    <BreakpointsContext.Provider value={configuration}>
      {children}
    </BreakpointsContext.Provider>
  )
}

export function useBreakpointsConfiguration() {
  return useContext(BreakpointsContext)
}
